import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { SchoolPerformance, RegionPerformance } from '../../types';

interface RankingTableProps {
  data: (SchoolPerformance | RegionPerformance)[];
  type: 'school' | 'region';
  title: string;
  limit?: number;
  highlightTop?: number;
  highlightBottom?: number;
  /** Order to rank in. 'asc' puts the lowest score first, for a panel of schools that need attention. */
  sortDirection?: 'asc' | 'desc';
  onRowClick?: (item: SchoolPerformance | RegionPerformance) => void;
}

const RankingTable: React.FC<RankingTableProps> = ({
  data,
  type,
  title,
  limit = 10,
  highlightTop = 3,
  highlightBottom = 0,
  sortDirection = 'desc',
  onRowClick,
}) => {
  // These amounts arrive in ₹ LAKHS. The old thresholds treated them as rupees,
  // so a 225.99 lakh figure rendered as a bare '₹225.998' with no unit at all.
  const formatCurrency = (value: number) => {
    if (Math.abs(value) >= 100) return `₹${(value / 100).toFixed(2)}Cr`;
    return `₹${value.toFixed(2)}L`;
  };

  const getRowBgColor = (index: number, total: number) => {
    if (index < highlightTop) {
      return 'bg-green-50 hover:bg-green-100';
    }
    if (highlightBottom > 0 && index >= total - highlightBottom) {
      return 'bg-red-50 hover:bg-red-100';
    }
    return 'hover:bg-gray-50';
  };

  const getRankBadgeColor = (rank: number) => {
    if (rank === 1) return 'bg-yellow-400 text-yellow-900';
    if (rank === 2) return 'bg-gray-300 text-gray-800';
    if (rank === 3) return 'bg-amber-600 text-white';
    return 'bg-gray-100 text-gray-600';
  };

  const getName = (item: SchoolPerformance | RegionPerformance): string => {
    if (type === 'region') {
      return (item as RegionPerformance).roName;
    }
    return (item as SchoolPerformance).schoolName;
  };

  const getParentName = (item: SchoolPerformance | RegionPerformance): string | undefined => {
    if (type === 'school') {
      return (item as SchoolPerformance).roName;
    }
    return undefined;
  };

  // Previously always sorted descending, which silently reversed any panel fed an
  // ascending list: the worst school landed at the bottom of "Needing Attention".
  const sortedData = [...data]
    .sort((a, b) => (sortDirection === 'asc'
      ? a.performanceScore - b.performanceScore
      : b.performanceScore - a.performanceScore))
    .slice(0, limit)
    .map((item, index) => ({
      ...item,
      rank: index + 1
    }));

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rank</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Sanctioned</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Received</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Spent</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Gap</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Utilization</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedData.map((item, index) => (
              <tr
                key={type === 'region' ? (item as RegionPerformance).roId : (item as SchoolPerformance).kvId}
                onClick={() => onRowClick?.(item)}
                className={`${getRowBgColor(index, sortedData.length)} ${onRowClick ? 'cursor-pointer' : ''} transition-colors`}
              >
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getRankBadgeColor(item.rank)}`}>
                    {item.rank}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{getName(item)}</div>
                  {getParentName(item) && (
                    <div className="text-xs text-gray-500">{getParentName(item)}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(item.totalSanctioned)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-medium text-blue-600">
                    {formatCurrency(item.totalReceived)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-medium text-green-600">
                    {formatCurrency(item.totalSpent)}
                  </span>
                </td>
                {/* Sanctioned − Spent: funds allotted but not yet spent. */}
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-medium text-gray-600">
                    {formatCurrency(item.totalSanctioned - item.totalSpent)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          item.overallUtilization >= 75
                            ? 'bg-green-500'
                            : item.overallUtilization >= 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(item.overallUtilization, 100)}%` }}
                      />
                    </div>
                    <span className={`text-sm font-bold ${
                      item.overallUtilization >= 75
                        ? 'text-green-600'
                        : item.overallUtilization >= 50
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`}>
                      {item.overallUtilization.toFixed(1)}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {onRowClick && (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RankingTable;
